import React, {
  useEffect,
  useCallback,
  useContext,
  useState,
  useRef,
} from "react";
import Aes from "../../utils/aes.encrypt.decrypt";
import { StoreUtil } from "../../utils/store";
import {
  __prod__,
  ISUSERLOOGED_KEY,
  LOGGED_USER_ID,
} from "../../utils/constants";
import Utils from "../../utils";
import AuthUtil from "../../utils/auth";
import {
  GetChunkAPICountDtl,
  GetMenuHierarchyList,
  GetSpecificUserMasterData,
} from "../../utils/api.constant";
import { eResultCode } from "../../utils/enum";
import useFetch from "../../hooks/useFetch";
import { alert } from "../toast-container/alert";
import { UserContext } from "../../context/user";
import { TContext, TMenu, TUser } from "../../types/config";

type TProps = {
  children: React.ReactNode;
};

export default function WindowPerformer(props: TProps) {
  const { post } = useFetch();
  const context: any = useContext(UserContext);
  const userRef = useRef<TUser>(context?.user);

  // Update ref whenever context user changes
  useEffect(() => {
    userRef.current = context?.user;
  }, [context?.user]);

  const onFocus = useCallback(async () => {
    const isUserLogged = Aes.decryptURLSafe(
      StoreUtil.getCookie(ISUSERLOOGED_KEY) ?? "false",
    );
    const currentUrl = window.location.href;

    const userId = Aes.decryptURLSafe(
      StoreUtil.getCookie(LOGGED_USER_ID) ?? "0",
    );

    const activeUser = userRef.current;

    // // TO PREVENT IF ANOTHER USER LOGGED IN ANOTHER TAB -- TO SYNC ALL THE NEW LOGGED IN USER DETAILS ON PREVIOUSLY ACTIVE TAB

    const cookieUserId = parseInt(userId as string);
    if (
      isUserLogged === "true" &&
      activeUser?.id &&
      cookieUserId > 0 &&
      activeUser.id !== cookieUserId
    ) {
      const user: TUser = await initializeUser();
      const menuHierarchy: any = await getMenuHierarchyList(); // Fetch menu data
      const chunkConfig: any = await getChunkConfiguration();

      // Check if the current URL matches any menu entityUrl
      const isMatchingUrl = menuHierarchy?.some((menu: TMenu) =>
        currentUrl.includes(menu?.entityUrl || ""),
      );

      await alert({
        title: "Login Activity Detected",
        type: "error",
        description: "System has detected another login activity!",
      });

      if (!isMatchingUrl && menuHierarchy?.length > 0) {
        Utils.redirectUrl(menuHierarchy[0].entityUrl);
      } else {
        window.location.reload();
      }

      context?.onUpdate?.({
        user: user,
        menuHierarchy: menuHierarchy,
        chunkSize: chunkConfig?.chunkSize,
        apiCount: chunkConfig?.apiCount,
      });
    }

    if (isUserLogged !== "true") {
      if (currentUrl.includes("/forgot-password")) return;

      AuthUtil.logout();
      if (!currentUrl.includes("/login")) {
        await alert({
          title: "Logout Activity Detected",
          type: "error",
          description: "System has detected logout activity!",
        });
        Utils.redirectUrl("/login");
      }
    } else if (!currentUrl.includes("/dashboard")) {
      const menus = await getMenuHierarchyList();
      if (menus?.length > 0) {
        Utils.redirectUrl(menus[0].entityUrl);
      }
    }
  }, [post, context]);

  const getMenuHierarchyList = async () => {
    try {
      const requestPayload = {
        data: {
          renderMenuRoleWise: true,
        },
      };
      const response = await post(GetMenuHierarchyList, requestPayload);
      const { data, dataResponse } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        return data;
      } else {
        console.error(`Error while fetching menu hierarchy: ${description}`);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const initializeUser = async () => {
    try {
      const requestPayload = {
        data: {},
      };
      const response = await post(GetSpecificUserMasterData, requestPayload);
      const { data, dataResponse } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        const value = data[0];
        return value;
      } else {
        AuthUtil.logout();
        console.error(`Error while initializing user: ${description}`);
      }
    } catch (error) {
      AuthUtil.logout();
      console.error(error);
    }
  };

  const getChunkConfiguration = async () => {
    try {
      const requestPayload = {
        data: {},
      };
      const response = await post(GetChunkAPICountDtl, requestPayload);
      const { data, dataResponse } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        return data;
      } else {
        console.error(`Error while chunk configuration: ${description}`);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    window.onfocus = onFocus;

    // Uncomment the below section to prevent inspect element in production mode
    if (__prod__) {
      const handleContextMenu = (e: MouseEvent) => {
        e.preventDefault();
      };

      const handleKeyDown = (e: KeyboardEvent) => {
        if (
          e.keyCode === 123 || // F12
          (e.ctrlKey && e.shiftKey && e.keyCode === 73) || // Ctrl+Shift+I
          (e.ctrlKey && e.shiftKey && e.keyCode === 74) || // Ctrl+Shift+J
          (e.ctrlKey && e.keyCode === 85) // Ctrl+U
        ) {
          e.preventDefault();
          return false;
        }
      };

      document.addEventListener("contextmenu", handleContextMenu);
      document.addEventListener("keydown", handleKeyDown);

      return () => {
        document.removeEventListener("contextmenu", handleContextMenu);
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [onFocus]);

  return <>{props.children}</>;
}
